import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Palette, User, Calendar, Sparkles } from 'lucide-react';
import { getImageUrl, getLocalizedField, handleImageError } from '@/utils/helpers';
import { useAutoTranslateFields } from '@/hooks/useAutoTranslate';
import { DualPrice } from '@/utils/priceUtils';

const API = process.env.REACT_APP_BACKEND_URL || '';

// 筛选选项配置
const FILTER_OPTIONS = [
  { id: 'all', labelKey: 'featuredArtworks.filter.all', icon: Sparkles },
  { id: '2020s', labelKey: 'featuredArtworks.filter.2020s', icon: Calendar, yearRange: [2020, 2029] },
  { id: '2010s', labelKey: 'featuredArtworks.filter.2010s', icon: Calendar, yearRange: [2010, 2019] },
  { id: '2000s', labelKey: 'featuredArtworks.filter.2000s', icon: Calendar, yearRange: [2000, 2009] },
  { id: 'classic', labelKey: 'featuredArtworks.filter.classic', icon: Calendar, yearRange: [0, 1999] },
];

// 需要自动翻译的语言
const AUTO_TRANSLATE_LANGS = ['ru', 'uk', 'fr', 'es', 'pt', 'ja', 'ko', 'ar', 'uz'];

// 单个作品卡片组件 - 支持自动翻译
const ArtworkCard = ({ artwork, onClick, t }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const needsTranslation = AUTO_TRANSLATE_LANGS.includes(currentLang);
  
  // 使用自动翻译 hook
  const { translations, isTranslating } = useAutoTranslateFields(
    artwork, 
    ['title', 'description', 'artist'], 
    currentLang
  );
  
  // 获取翻译后的文本
  const displayTitle = needsTranslation ? (translations.title || artwork.title) : getLocalizedField(artwork, 'title', currentLang);
  const displayDesc = needsTranslation ? (translations.description || artwork.description) : getLocalizedField(artwork, 'description', currentLang);
  const displayArtist = artwork.artist || artwork.creator_name || t('featuredArtworks.unknownArtist');
  
  return (
    <div
      className="flex-shrink-0 w-72 group cursor-pointer bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300"
      style={{ scrollSnapAlign: 'start' }}
      onClick={onClick}
      data-testid={`artwork-card-${artwork.id}`}
    >
      {/* 作品图片 */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
        <img
          src={getImageUrl(artwork.image_url)}
          alt={displayTitle}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={handleImageError}
        />
        {/* 原作出售标识 */}
        {artwork.is_original_for_sale && artwork.original_price > 0 && (
          <div className="absolute top-3 left-3 z-10" data-testid={`sale-badge-${artwork.id}`}>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
              <span className="w-1.5 h-1.5 bg-white rounded-full" />
              {t('artwork.forSale', 'FOR SALE')}
            </span>
          </div>
        )}
        {/* 已售出标识 */}
        {artwork.is_sold && (
          <div className="absolute top-3 left-3 z-10">
            <span className="inline-flex items-center px-2.5 py-1 bg-gray-800/80 text-white text-xs font-medium rounded-full">
              {t('artwork.sold', 'SOLD')}
            </span>
          </div>
        )}
        {/* 悬停遮罩 */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* 悬停显示详情 */}
        <div className="absolute inset-x-0 bottom-0 p-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
          <p className="text-white text-sm line-clamp-2 mb-2">
            {displayDesc || t('featuredArtworks.noDescription')}
          </p>
          <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
            {t('featuredArtworks.viewDetail')}
          </span>
        </div>
      </div>

      {/* 作品信息 - 在卡片内 */}
      <div className="p-4">
        <h3 className="text-base font-medium text-amber-900 truncate group-hover:text-amber-600 transition-colors">
          {displayTitle}
          {isTranslating && <span className="ml-1 text-xs text-amber-400">...</span>}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-sm text-amber-600/70">
          <User className="w-3.5 h-3.5" />
          <span className="truncate">{displayArtist}</span>
          {artwork.year && (
            <>
              <span className="text-amber-400">·</span>
              <span>{artwork.year}</span>
            </>
          )}
        </div>
        {artwork.is_original_for_sale && artwork.original_price > 0 && (
          <div className="mt-2">
            <DualPrice kwhAmount={artwork.original_price} size="sm" />
          </div>
        )}
      </div>
    </div>
  );
};

const FeaturedArtworks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [allArtworks, setAllArtworks] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  // 获取作品数据
  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const res = await axios.get(`${API}/api/artworks?limit=50&active_only=true`);
        const data = res.data || [];
        setAllArtworks(data);
        setArtworks(data);
      } catch (err) {
        console.error('Failed to fetch artworks:', err);
        setAllArtworks([]);
        setArtworks([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArtworks();
  }, []);

  // 筛选作品
  useEffect(() => {
    if (activeFilter === 'all') {
      setArtworks(allArtworks);
    } else {
      const filterOption = FILTER_OPTIONS.find(f => f.id === activeFilter);
      if (filterOption?.yearRange) {
        const [minYear, maxYear] = filterOption.yearRange;
        const filtered = allArtworks.filter(artwork => {
          const year = parseInt(artwork.year) || 0;
          return year >= minYear && year <= maxYear;
        });
        setArtworks(filtered);
      }
    }
    // 筛选后重置滚动位置
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [activeFilter, allArtworks]);

  // 更新滚动按钮状态
  const updateScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons();
      return () => el.removeEventListener('scroll', updateScrollButtons);
    }
  }, [artworks]);

  // 滚动控制
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320; // 卡片宽度 + gap
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // 点击作品
  const handleArtworkClick = (artworkId) => {
    navigate(`/artwork/${artworkId}`);
  };

  if (loading) {
    return (
      <section className="py-16 px-6 bg-amber-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse flex space-x-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-72 h-80 bg-amber-100 rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (artworks.length === 0) {
    return null; // 没有作品时不显示此板块
  }

  return (
    <section className="py-16 px-6 bg-amber-50/30" data-testid="featured-artworks-section">
      <div className="max-w-7xl mx-auto">
        {/* 标题区域 */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-amber-900">
              {t('featuredArtworks.title')}
            </h2>
            <p className="text-amber-700/70 mt-1 text-sm">
              {t('featuredArtworks.badge')}
            </p>
          </div>
          
          {/* 滚动控制按钮 */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`p-3 rounded-full border transition-all ${
                canScrollLeft 
                  ? 'border-amber-300 hover:border-amber-600 hover:bg-amber-600 hover:text-white text-amber-700' 
                  : 'border-amber-200 text-amber-300 cursor-not-allowed'
              }`}
              aria-label="向左滚动"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`p-3 rounded-full border transition-all ${
                canScrollRight 
                  ? 'border-amber-300 hover:border-amber-600 hover:bg-amber-600 hover:text-white text-amber-700' 
                  : 'border-amber-200 text-amber-300 cursor-not-allowed'
              }`}
              aria-label="向右滚动"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 筛选标签栏 */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
          {FILTER_OPTIONS.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeFilter === filter.id;
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                }`}
                data-testid={`filter-${filter.id}`}
              >
                <Icon className="w-4 h-4" />
                {t(filter.labelKey)}
              </button>
            );
          })}
        </div>

        {/* 作品滚动区域 */}
        <div className="relative">
          {artworks.length === 0 ? (
            /* 筛选结果为空 */
            <div className="flex flex-col items-center justify-center py-16 text-amber-500">
              <Calendar className="w-12 h-12 mb-3" />
              <p className="text-lg">{t('featuredArtworks.noArtworksInPeriod')}</p>
              <button
                onClick={() => setActiveFilter('all')}
                className="mt-4 text-purple-600 hover:text-purple-700 text-sm font-medium"
              >
                {t('featuredArtworks.viewAllArtworks')}
              </button>
            </div>
          ) : (
            <div
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto scrollbar-hide pb-4 -mx-2 px-2"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {artworks.map((artwork, index) => (
                <ArtworkCard 
                  key={artwork.id || index}
                  artwork={artwork}
                  onClick={() => handleArtworkClick(artwork.id)}
                  t={t}
                />
              ))}
            </div>
          )}

          {/* 移动端滚动指示器 */}
          {artworks.length > 0 && (
            <div className="flex md:hidden justify-center mt-4 gap-1">
              {artworks.slice(0, Math.min(5, artworks.length)).map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-amber-300"
                />
              ))}
            </div>
          )}
        </div>

        {/* 查看更多按钮 */}
        <div className="text-center mt-10">
          <button
            onClick={() => navigate('/artcanvas')}
            className="inline-flex items-center gap-2 px-8 py-3 border border-amber-600 text-amber-700 rounded-full hover:bg-amber-600 hover:text-white transition-all duration-300"
            data-testid="view-all-artworks-btn"
          >
            {t('featuredArtworks.viewMore')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 隐藏滚动条样式 */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default FeaturedArtworks;
